/* Date helpers for the admin panel.
 *
 * Staarvani operates on IST, so "today" means the IST calendar day, not the
 * viewer's local day and not a UTC day. India has no daylight saving, so a fixed
 * +05:30 offset is safe and avoids pulling in a timezone library. */

export const IST_OFFSET_MINUTES = 330;
const MS_PER_MINUTE = 60000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * API date format: "YYYY-MM-DD HH:mm:ss.SSS +0000".
 * Always serialised in UTC -- the backend parses the offset, so the instant is
 * preserved whatever timezone the admin's machine is in.
 */
export function toApiDate(date) {
  const pad = (n, width = 2) => String(n).padStart(width, '0');
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}.` +
    `${pad(date.getUTCMilliseconds(), 3)} +0000`
  );
}

/** The IST calendar date containing `at`, as {year, month, day} (month 0-based). */
function istCalendarDate(at) {
  // Shifting the instant by the offset makes getUTC* read IST civil time.
  const shifted = new Date(at.getTime() + IST_OFFSET_MINUTES * MS_PER_MINUTE);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
}

/**
 * The UTC instants bounding the IST calendar day containing `at`:
 * 00:00:00.000 IST through 23:59:59.999 IST.
 *
 * Independent of the browser's timezone -- an admin in London and one in Mumbai
 * asking for "today" get the same window.
 */
export function istDayRange(at = new Date()) {
  const { year, month, day } = istCalendarDate(at);
  const startMs = Date.UTC(year, month, day) - IST_OFFSET_MINUTES * MS_PER_MINUTE;
  return {
    start: new Date(startMs),
    end: new Date(startMs + MS_PER_DAY - 1),
  };
}

/** The last `count` IST calendar days ending with today, as a UTC instant range. */
export function istLastDaysRange(count, at = new Date()) {
  const { start } = istDayRange(at);
  return {
    start: new Date(start.getTime() - (count - 1) * MS_PER_DAY),
    end: at,
  };
}

/* ── Trend chart ranges ─────────────────────────────────────────────────────
   All bounded on IST calendar edges, so "this month" starts at 1st 00:00 IST
   wherever the admin happens to be sitting. */

function istMidnight(year, month, day) {
  return new Date(Date.UTC(year, month, day) - IST_OFFSET_MINUTES * MS_PER_MINUTE);
}

/**
 * How finely to slice a window. A year drawn a day at a time is 365 columns in a
 * chart a thousand pixels wide -- unreadable, and every label collides.
 */
export function bucketForSpan(start, end) {
  const days = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
  if (days <= 70) return 'day';
  if (days <= 400) return 'week';
  return 'month';
}

/**
 * A pair of yyyy-mm-dd strings from date inputs -> the IST window they name, running
 * from the first day's midnight through the last day's final instant so the end date
 * the admin picked is included rather than excluded.
 *
 * Returns null while the pair is incomplete or backwards, so callers can hold what is
 * already on screen rather than requesting a window that means nothing.
 */
export function resolveDayRange(custom = {}) {
  if (!custom.start || !custom.end) return null;
  const [sy, sm, sd] = String(custom.start).split('-').map(Number);
  const [ey, em, ed] = String(custom.end).split('-').map(Number);
  if (!sy || !sm || !sd || !ey || !em || !ed) return null;

  const start = istMidnight(sy, sm - 1, sd);
  const end = new Date(istMidnight(ey, em - 1, ed + 1).getTime() - 1);
  if (end <= start) return null;

  return { start, end, bucket: bucketForSpan(start, end) };
}

export const TREND_RANGES = [
  { value: '30d', label: 'Last 30 days' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: 'year', label: 'This year' },
  { value: 'lastyear', label: 'Last year' },
  { value: 'custom', label: 'Custom range…' },
];

/**
 * A range key -> { start, end, bucket }, or null when a custom range is not yet
 * complete. `custom` is { start, end } as yyyy-mm-dd from date inputs.
 *
 * "This week/month/year" run from the start of that period to now; "last 3/6
 * months" are rolling windows ending now; "last year" is the whole previous
 * calendar year.
 */
export function resolveTrendRange(value, custom = {}) {
  const now = new Date();
  const { year, month, day } = istCalendarDate(now);

  let start;
  let end = now;

  switch (value) {
    case 'week': {
      // Monday-based, matching Postgres date_trunc('week', ...).
      const weekday = (new Date(Date.UTC(year, month, day)).getUTCDay() + 6) % 7;
      start = istMidnight(year, month, day - weekday);
      break;
    }
    case 'month':
      start = istMidnight(year, month, 1);
      break;
    case '3m':
      start = istMidnight(year, month - 3, day);
      break;
    case '6m':
      start = istMidnight(year, month - 6, day);
      break;
    case 'year':
      start = istMidnight(year, 0, 1);
      break;
    case 'lastyear':
      start = istMidnight(year - 1, 0, 1);
      // Through the final instant of 31 Dec IST, not the start of 1 Jan.
      end = new Date(istMidnight(year, 0, 1).getTime() - 1);
      break;
    case 'custom':
      return resolveDayRange(custom);
    case '30d':
    default: {
      const { start: s } = istLastDaysRange(30, now);
      start = s;
      break;
    }
  }

  return { start, end, bucket: bucketForSpan(start, end) };
}
